// library imports
import {
  Button,
  Input,
  Modal,
  Popconfirm,
  Table,
  Typography,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState, useContext } from "react";
// context imports
import { IndexContext } from "../../context/IndexContext";
// hook imports
import useFetch from "../../hooks/useFetch";
// config imports
import { VERIFIER_ROLE_ID } from "../../configs/configs";

export default function SopPage() {
  // load index context
  const { messageApi, siteSelected, authenticationState } =
    useContext(IndexContext);

  // fetch wrapper
  const [fetchWrapper] = useFetch();

  const [tableLoading, setTableLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  // upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [customName, setCustomName] = useState("");
  const pendingFileRef = useRef(null);

  // edit name modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editName, setEditName] = useState("");
  const editRecordRef = useRef(null);

  // ── Table columns ─────────────────────────────────────────────────────────

  const columns = [
    {
      title: "Name",
      dataIndex: "Name",
      key: "Name",
      render: (name, record) => (
        <Typography.Link href={record.url} target="_blank" rel="noreferrer">
          {name}
        </Typography.Link>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record) => (
        <>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{
              marginRight: 4,
              color:
                authenticationState.user_type >= VERIFIER_ROLE_ID
                  ? "gray"
                  : "blue",
            }}
            disabled={authenticationState.user_type >= VERIFIER_ROLE_ID}
          />
          <Popconfirm
            title="Delete file"
            description="Are you sure you want to delete this file?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={authenticationState.user_type >= VERIFIER_ROLE_ID}
            />
          </Popconfirm>
        </>
      ),
    },
  ];

  // fetch file list from backend
  function fetchFileList() {
    setTableLoading(true);

    fetchWrapper({
      endpoint_url: "sop/getFileList",
      params: { site_id: siteSelected },
      onSuccess: (response) => {
        setFileList(response.data);
        setTableLoading(false);
      },
      onFinish: () => setTableLoading(false),
    });
  }

  // ── Upload ────────────────────────────────────────────────────────────────

  function beforeUpload(file) {
    pendingFileRef.current = file;
    setUploadModalOpen(true);
    return false;
  }

  function handleUploadConfirm() {
    const file = pendingFileRef.current;
    if (!file) return;

    setUploadLoading(true);

    const formData = new FormData();
    formData.append("uploaded-files", file);
    formData.append("site_id", siteSelected);
    formData.append("name", customName.trim());

    fetchWrapper({
      endpoint_url: "sop/uploadFile",
      set_content_type_header: false,
      body: formData,
      onSuccess: (response) => {
        if ("error" in response) {
          messageApi.open({ type: "error", content: response.error });
        } else {
          messageApi.open({ type: "success", content: response.msg });
          fetchFileList();
          setUploadModalOpen(false);
          pendingFileRef.current = null;
          setCustomName("");
        }
        setUploadLoading(false);
      },
      onFetchError: (error) => {
        messageApi.open({ type: "error", content: `Upload failed: ${error}` });
        setUploadLoading(false);
      },
    });
  }

  function handleUploadCancel() {
    if (uploadLoading) return;
    setUploadModalOpen(false);
    pendingFileRef.current = null;
    setCustomName("");
  }

  // ── Edit name ─────────────────────────────────────────────────────────────

  function handleEdit(record) {
    editRecordRef.current = record;
    setEditName(record.Name);
    setEditModalOpen(true);
  }

  function handleEditConfirm() {
    const record = editRecordRef.current;
    if (!record) return;

    setEditLoading(true);

    fetchWrapper({
      endpoint_url: "sop/updateFileName",
      params: { ID: record.ID, name: editName.trim() },
      onSuccess: (response) => {
        if (response.data) {
          messageApi.open({ type: "success", content: response.message });
          fetchFileList();
          setEditModalOpen(false);
          editRecordRef.current = null;
          setEditName("");
        } else {
          messageApi.open({ type: "error", content: response.message });
        }
        setEditLoading(false);
      },
      onFetchError: () => {
        messageApi.open({
          type: "error",
          content: "Failed to update file name",
        });
        setEditLoading(false);
      },
    });
  }

  function handleEditCancel() {
    if (editLoading) return;
    setEditModalOpen(false);
    editRecordRef.current = null;
    setEditName("");
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  function handleDelete(record) {
    fetchWrapper({
      endpoint_url: "sop/deleteFile",
      params: { ID: record.ID },
      onSuccess: (response) => {
        messageApi.open({ type: "success", content: response.message });
        fetchFileList();
      },
      onFetchError: () => {
        messageApi.open({ type: "error", content: "Failed to delete file" });
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      fetchFileList();
    }
  }, [siteSelected]);

  return (
    <>
      <Upload
        beforeUpload={beforeUpload}
        showUploadList={false}
        rootClassName="upload-button-max-width"
      >
        <Button
          type="primary"
          icon={<UploadOutlined />}
          style={{ width: "100%" }}
          disabled={authenticationState.user_type >= VERIFIER_ROLE_ID}
        >
          Click to Upload
        </Button>
      </Upload>

      <Table
        style={{ marginTop: 16 }}
        columns={columns}
        dataSource={fileList}
        loading={tableLoading}
        pagination={{ pageSize: 10 }}
        size="small"
      />

      {/* Upload modal */}
      <Modal
        title="Upload File"
        // open
        open={uploadModalOpen}
        // confirmation
        onOk={handleUploadConfirm}
        okText="Upload"
        okButtonProps={{ disabled: !customName.trim() }}
        confirmLoading={uploadLoading}
        // cancellation
        onCancel={handleUploadCancel}
        // close
        maskClosable={false}
        clearOnDestroy
        // style
        styles={{ footer: { textAlign: "center" } }}
      >
        <Typography.Text
          type="secondary"
          style={{ display: "block", marginBottom: 8 }}
        >
          File: {pendingFileRef.current?.name}
        </Typography.Text>
        <Input
          placeholder="Enter a name for this file"
          value={customName}
          autoComplete="off"
          onChange={(e) => setCustomName(e.target.value)}
          onPressEnter={customName.trim() ? handleUploadConfirm : undefined}
          autoFocus
        />
      </Modal>

      {/* Edit name modal */}
      <Modal
        title="Edit File Name"
        // open
        open={editModalOpen}
        // confirmation
        onOk={handleEditConfirm}
        okText="Save"
        okButtonProps={{ disabled: !editName.trim() }}
        confirmLoading={editLoading}
        // cancellation
        onCancel={handleEditCancel}
        // close
        maskClosable={false}
        clearOnDestroy
        // style
        styles={{ footer: { textAlign: "center" } }}
      >
        <Input
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onPressEnter={editName.trim() ? handleEditConfirm : undefined}
          autoFocus
        />
      </Modal>
    </>
  );
}
